import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { validatePostalCode } from '../../validationUtils';
import { getAddress } from '../../utilities';
import '../student/Newstudent.css';
import { useAuth } from '../../context/AuthContext';
import IBANInput from '../../components/IBANInput.js';

function EditDocent() {
  const { userInfo } = useAuth();
  const role = userInfo?.role;
  const is_single_user = userInfo?.is_single_user;

  const { docentId } = useParams();
  const navigate = useNavigate();
  const token = sessionStorage.getItem('jwtToken');

  const [allIds, setAllIds] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);

  const [postalcodeValidation, setPostalCodeValidation] = useState({ valid: true, message: '' });
  const [userStatus, setUserStatus] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [emailExists, setEmailExists] = useState(false);
  const [locations, setLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);

  const [values, setValues] = useState({
    firstname: '',
    lastname: '',
    birthdate: '',
    gender: '',
    datein: '',
    dateout: '',
    street: '',
    housenr: '',
    postalcode: '',
    city: '',
    iban: '',
    accountowner: '',
    comments: '',
    cv: '',
    email: '',
    mobile: '',
    customer_docent_number: '',
  });

  const handleEmailBlur = async () => {
    if (values.email.trim() === '') return;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/auth/check-email-existence`,
        {
          params: { email: values.email },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setEmailExists(response.data.exists && response.data.email !== values.email);
    } catch (error) {
      console.error('Error checking email existence:', error);
    }
  };

  const fetchUserLoginData = useCallback(
    async (email) => {
      if (!email) {
        setUserStatus('Geen waarde');
        setUserRole('Geen waarde');
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/auth/logindata/${email}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 204) {
          setUserStatus('Geen waarde');
          setUserRole('Geen waarde');
        } else if (response.status === 200) {
          setUserStatus(response.data.status || 'Geen waarde');
          setUserRole(response.data.role || 'Geen waarde');
        } else {
          console.error('Unexpected response status:', response.status);
          setUserStatus('Error fetching data');
          setUserRole('Error fetching data');
        }
      } catch (error) {
        console.error('Error fetching user login data:', error);
        setUserStatus('Fout');
        setUserRole('Fout');
      }
    },
    [token]
  );

  const fetchAllIds = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/docent/allids`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAllIds(res.data.ids || []);
    } catch (err) {
      console.error('Fout bij ophalen alle docent-IDs:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchUserLoginData(values.email);
  }, [fetchUserLoginData, values.email]);

  useEffect(() => {
    fetchAllIds();
  }, [fetchAllIds]);

  useEffect(() => {
    if (allIds.length === 0 || !docentId) return;
    setCurrentIndex(allIds.indexOf(docentId));
  }, [allIds, docentId]);

  useEffect(() => {
    const fetchDocent = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/docent/getdocent/${docentId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (res.status === 200) {
          setValues({
            firstname: res.data.Data.firstname || '',
            lastname: res.data.Data.lastname || '',
            email: res.data.Data.email || '',
            mobile: res.data.Data.mobile || '',
            birthdate: res.data.Data.birthdate || '',
            gender: res.data.Data.gender || '',
            datein: res.data.Data.datein || '',
            dateout: res.data.Data.dateout || '',
            street: res.data.Data.street || '',
            housenr: res.data.Data.housenr || '',
            postalcode: res.data.Data.postalcode || '',
            city: res.data.Data.city || '',
            iban: res.data.Data.iban || '',
            accountowner: res.data.Data.accountowner || '',
            comments: res.data.Data.comments || '',
            cv: res.data.Data.cv || '',
            customer_docent_number: res.data.Data.customer_docent_number || '',
          });

          setLocations(res.data.Data.locations || []);
          setSelectedLocations(res.data.Data.assigned_locations || []);
        }
      } catch (err) {
        console.error('Error fetching docent data:', err);
      }
    };

    fetchDocent();
  }, [docentId, token]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (emailExists) {
      alert('Het e-mailadres bestaat al in het systeem.');
      return;
    }

    try {
      const payload = {
        ...values,
        dateout:
          values.dateout === '0000-00-00' || values.dateout === ''
            ? null
            : values.dateout,
        location_ids:
          role === 'super_admin' && is_single_user === false
            ? selectedLocations
            : [],
      };

      const res = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/docent/updatedocent/${docentId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res.data.Status === 'Success') {
        navigate('/docenten');
      } else {
        alert('Error: ' + res.data.status);
      }
    } catch (error) {
      console.error('Error in updating docent:', error);
    }
  };

  const handleCheckboxChange = (locId) => {
    setSelectedLocations((prev) =>
      prev.includes(locId) ? prev.filter((x) => x !== locId) : [...prev, locId]
    );
  };

  const handleCancel = () => {
    navigate('/alldocenten');
  };

  const checkAndDeactivateUser = async () => {
    try {
      const checkResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/auth/check-email-in-users?email=${values.email}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (checkResponse.data.exists) {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/auth/deactivate`,
          { email: values.email },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        fetchUserLoginData(values.email);
      }
    } catch (error) {
      console.error('Error in checking email existence or deactivating user:', error);
    }
  };

  const checkAndActivateUser = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/auth/activate`,
        {
          name: `${values.firstname} ${values.lastname}`,
          email: values.email,
          role: 'docent',
          location_ids: selectedLocations,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.Status === 'Success') {
        fetchUserLoginData(values.email);
      } else {
        alert('Mogelijk is er iets misgegaan');
      }
    } catch (error) {
      console.error('Error in registerOrActivateUser:', error);
    }
  };

  return (
    <div>
      <div className="bg-white p-4 pb-xxl">
        <div>
          <h2 className="text-center">Bewerk Docent</h2>
        </div>

        <hr className="seperator" />

        <div className="d-flex justify-content-between align-items-center mb-3">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => {
              if (currentIndex > 0) {
                const prevId = allIds[currentIndex - 1];
                navigate(`/editdocent/${prevId}`);
              }
            }}
            disabled={currentIndex <= 0}
          >
            <i className="bi bi-arrow-left"></i> Vorige
          </button>

          <span>
            Docent {currentIndex + 1} van {allIds.length}
          </span>

          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => {
              if (currentIndex < allIds.length - 1) {
                const nextId = allIds[currentIndex + 1];
                navigate(`/editdocent/${nextId}`);
              }
            }}
            disabled={currentIndex < 0 || currentIndex >= allIds.length - 1}
          >
            Volgende <i className="bi bi-arrow-right"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="form-docent">
          <div className="row">
            <div className="mb-3 col">
              <button
                type="button"
                className="btn btn-secondary w-50 rounded-0"
                onClick={checkAndActivateUser}
              >
                Enable inlog
              </button>
            </div>

            <div className="mb-3 col">
              <button
                type="button"
                className="btn btn-secondary w-50 rounded-0"
                onClick={checkAndDeactivateUser}
              >
                Disable inlog
              </button>
            </div>
          </div>

          <div className="mb-3 col">
            <p>Status: {userStatus ? userStatus : 'Loading...'}</p>
            <p>Role: {userRole ? userRole : 'Loading...'}</p>
          </div>

          <div className="mb-3 col">
            <p>Docentnummer: {values.customer_docent_number || ''}</p>
          </div>

          <div className="row">
            <div className="mb-3 col">
              <label htmlFor="firstname">Voornaam</label>
              <label style={{ color: 'red' }}>*</label>
              <input
                className="form-control rounded-0"
                type="text"
                name="firstname"
                placeholder="Voornaam"
                required
                value={values.firstname}
                onChange={(e) => setValues({ ...values, firstname: e.target.value })}
              />
            </div>

            <div className="mb-3 col">
              <label htmlFor="lastname">Achternaam</label>
              <label style={{ color: 'red' }}>*</label>
              <input
                className="form-control rounded-0"
                type="text"
                name="lastname"
                placeholder="Achternaam"
                required
                value={values.lastname}
                onChange={(e) => setValues({ ...values, lastname: e.target.value })}
              />
            </div>
          </div>

          <div className="row">
            <div className="mb-3 col">
              <label htmlFor="birthdate">Geboortedatum</label>
              <label style={{ color: 'red' }}>*</label>
              <input
                className="form-control rounded-0"
                type="date"
                name="birthdate"
                placeholder="Geboortedatum"
                required
                value={values.birthdate}
                onChange={(e) => setValues({ ...values, birthdate: e.target.value })}
              />
            </div>

            <div className="mb-3 col">
              <label htmlFor="gender">Geslacht</label>
              <select
                className="form-control rounded-0"
                name="gender"
                value={values.gender}
                onChange={(e) => setValues({ ...values, gender: e.target.value })}
              >
                <option value="">Maak een keuze</option>
                <option value="Male">Man</option>
                <option value="Female">Vrouw</option>
                <option value="Other">Anders</option>
              </select>
            </div>
          </div>

          <div className="row">
            <div className="mb-3 col">
              <label htmlFor="datein">Datum Inschrijving</label>
              <label style={{ color: 'red' }}>*</label>
              <input
                className="form-control rounded-0"
                type="date"
                name="datein"
                placeholder="Datum Inschrijving"
                required
                value={values.datein}
                onChange={(e) => setValues({ ...values, datein: e.target.value })}
              />
            </div>

            <div className="mb-3 col">
              <label htmlFor="dateout">Datum Uitschrijving</label>
              <input
                className="form-control rounded-0"
                type="date"
                name="dateout"
                placeholder="Datum Uitschrijving"
                value={values.dateout}
                onChange={(e) => setValues({ ...values, dateout: e.target.value })}
              />
            </div>
          </div>

          <div className="row">
            <div className="mb-3 col">
              <label htmlFor="postalcode">Postcode</label>
              <input
                className={`form-control rounded-0 ${postalcodeValidation.valid ? '' : 'is-invalid'}`}
                type="text"
                name="postalcode"
                placeholder="Postcode"
                value={values.postalcode}
                onChange={(e) => {
                  setValues({ ...values, postalcode: e.target.value });
                  validatePostalCode(e.target.value);
                }}
                onBlur={(e) => {
                  const isValid = validatePostalCode(e.target.value);
                  if (!isValid) {
                    setPostalCodeValidation({ valid: false, message: 'Ongeldige Postcode' });
                  } else {
                    setPostalCodeValidation({ valid: true, message: '' });
                  }
                }}
              />
              {postalcodeValidation.valid ? null : (
                <div className="invalid-feedback">{postalcodeValidation.message}</div>
              )}
            </div>

            <div className="mb-3 col">
              <label htmlFor="housenr">Huisnummer</label>
              <input
                className="form-control rounded-0"
                type="text"
                name="housenr"
                placeholder="Huisnummer"
                value={values.housenr}
                onChange={(e) => setValues({ ...values, housenr: e.target.value })}
                onBlur={() => getAddress(values, setValues, postalcodeValidation)}
              />
            </div>
          </div>

          <div className="row">
            <div className="mb-3 col">
              <label htmlFor="street">Straat</label>
              <input
                className="form-control rounded-0"
                type="text"
                name="street"
                placeholder="Straat"
                readOnly
                value={values.street}
                style={{ backgroundColor: '#FAFAFA' }}
              />
            </div>

            <div className="mb-3 col">
              <label htmlFor="city">Plaats</label>
              <input
                className="form-control rounded-0"
                type="text"
                name="city"
                placeholder="Plaats"
                readOnly
                value={values.city}
                style={{ backgroundColor: '#FAFAFA' }}
              />
            </div>
          </div>

          <div className="row">
            <IBANInput
              value={values.iban}
              onChange={(iban) => setValues((v) => ({ ...v, iban }))}
            />

            <div className="mb-3 col">
              <label htmlFor="accountowner">Rekeninghouder</label>
              <input
                className="form-control rounded-0"
                type="text"
                name="accountowner"
                placeholder="Rekeninghouder"
                value={values.accountowner}
                onChange={(e) => setValues({ ...values, accountowner: e.target.value })}
              />
            </div>
          </div>

          <div className="mb-3">
            <label htmlFor="comments">Opmerkingen</label>
            <textarea
              className="form-control rounded-0"
              rows="5"
              cols="50"
              name="comments"
              placeholder="Opmerkingen"
              value={values.comments}
              onChange={(e) => setValues({ ...values, comments: e.target.value })}
            />
          </div>

          <div className="mb-3">
            <label htmlFor="cv">CV</label>
            <input
              className="form-control rounded-0"
              type="text"
              name="cv"
              placeholder="Link naar CV of CV data"
              value={values.cv}
              onChange={(e) => setValues({ ...values, cv: e.target.value })}
            />
          </div>

          <div className="row">
            <div className="mb-3 col">
              <label htmlFor="email">Email</label>
              <label style={{ color: 'red' }}>*</label>
              <input
                className="form-control rounded-0"
                type="email"
                name="email"
                placeholder="Email"
                required
                value={values.email}
                onChange={(e) => setValues({ ...values, email: e.target.value })}
                onBlur={handleEmailBlur}
              />
              {emailExists && (
                <div style={{ color: 'red' }}>Dit e-mailadres is al in gebruik.</div>
              )}
            </div>

            <div className="mb-3 col">
              <label htmlFor="mobile">Mobiel</label>
              <input
                className="form-control rounded-0"
                type="text"
                name="mobile"
                placeholder="Mobiel"
                value={values.mobile}
                onChange={(e) => setValues({ ...values, mobile: e.target.value })}
              />
            </div>
          </div>

          {role === 'super_admin' && !is_single_user && (
            <div className="mb-3">
              <label>Koppel locaties</label>
              <div className="d-flex flex-wrap">
                {Array.isArray(locations) && locations.length > 0 ? (
                  locations.map((loc) => (
                    <div key={loc.location_id} className="form-check me-3">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`loc-${loc.location_id}`}
                        checked={selectedLocations.includes(loc.location_id)}
                        onChange={() => handleCheckboxChange(loc.location_id)}
                      />
                      <label className="form-check-label" htmlFor={`loc-${loc.location_id}`}>
                        {loc.location_name}
                      </label>
                    </div>
                  ))
                ) : (
                  <p>Geen locaties beschikbaar</p>
                )}
              </div>
            </div>
          )}

          <hr className="seperator" />

          <div className="row">
            <div className="col-md-6">
              <button
                type="button"
                className="btn btn-secondary w-100 rounded-0"
                onClick={handleCancel}
              >
                Annuleren
              </button>
            </div>

            <div className="col-md-6">
              <button type="submit" className="btn btn-secondary w-100 rounded-0">
                Opslaan
              </button>
            </div>
          </div>

          <div className="spacer-bottom"></div>
        </form>
      </div>
    </div>
  );
}

export default EditDocent;